import { defineStore } from 'pinia'
import { $cookies } from '@/utils/utils'
import { useClientStore } from '@/stores/ClientStore'
import { useLanguageStore } from '@/stores/LanguageStore'
import { useUserStore } from '@/stores/UserStore'
import { pick as _pick } from 'lodash'
import User from '@/classes/users/User'
import { useTrackingSession, useTrackingUser } from '@/vendors/tracking'
import store from '@/store'

export const useAuthenticationStore = defineStore('AuthenticationStore', {
  state: () => ({
    auth_user: {},
    auth_token: null,
    data_export: {},
    loader: false,
    error: {}
  }),
  getters: {
    getAuthUser: state => () => state.$getItem({
      resource: 'auth_user',
      fetch: () => state.fetchAuthUser()
    }),
    getAuthUserDataExport: state => () => state.$getItem({
      resource: 'data_export',
      fetch: () => state.fetchAuthUserDataExport()
    }),
    authUser: state => state.$getItemFromState('auth_user'),
    isAuthUser: state => !!state.authUser,
    isAuthenticated: state => !!state.isAuthUser && !!state.auth_token
  },
  actions: {
    initAuthentication () {
      return new Promise((resolve, reject) => {
        if (this.isAuthUser) {
          resolve(this.authUser)
        } else if ($cookies.get('authToken')) {
          return this.fetchAuthUser()
            .then(user => {
              const { getClient } = useClientStore()
              const { trackSessionStarted } = useTrackingSession()
              const { trackUser, trackUserClient } = useTrackingUser()

              const { client } = getClient()

              trackSessionStarted({ user })
              trackUser({ user })
              trackUserClient({ client: client.value })

              resolve(user)
            })
            .catch(error => reject(error))
        } else {
          resolve(null)
        }
      })
    },
    setAuthToken (auth_token) {
      $cookies.set({ name: 'authToken', value: auth_token })
      this.auth_token = auth_token
    },
    fetchAuthUser () {
      return this.$fetchItem({
        endpoint: '/auth/user',
        resource: 'auth_user',
        model: User
      }).then(user => {
        const { setLocale } = useLanguageStore()

        setLocale(user.language)
        store.commit('auth/setUser', user)

        return user
      })
    },
    fetchAuthUserDataExport () {
      return this.$fetchItem({
        endpoint: '/auth/user/data_export',
        resource: 'data_export',
        model: User
      })
    },
    postAuthUser ({ params, token }) {
      return this.$postItem({
        endpoint: '/auth/user',
        resource: 'auth_user',
        model: User,
        params: {
          user: params,
          token
        }
      }).then(user => {
        const { setLocale } = useLanguageStore()
        const { trackSessionStarted, trackUserRegistered } = useTrackingSession()

        setLocale(user.language)
        store.commit('auth/setUser', user)

        trackSessionStarted({ user })
        trackUserRegistered({ method: 'email' })

        return user
      })
    },
    patchAuthUser ({ params }) {
      const { trackUser } = useTrackingUser()

      return this.$patchItem({
        endpoint: '/auth/user',
        resource: 'auth_user',
        model: User,
        params: {
          user: _pick(new User(params), Object.keys(params))
        }
      })
        .then(user => {
          trackUser({ user })

          return user
        })
    },
    postAuthUserPassword ({ params }) {
      return this.$postItem({
        endpoint: '/auth/user/forgot_password',
        params: {
          user: params
        }
      })
    },
    patchAuthUserPassword ({ params, token }) {
      return this.$patchItem({
        endpoint: '/auth/user/password',
        params: {
          user: params,
          token
        }
      })
    },
    patchAuthUserImage ({ params }) {
      const { trackUser } = useTrackingUser()

      return this.$patchItem({
        endpoint: '/auth/user',
        resource: 'auth_user',
        model: User,
        params,
        config: { headers: { 'Content-Type': 'multipart/form-data' } }
      })
        .then(user => {
          trackUser({ user })

          return user
        })
    },
    login ({ params }) {
      return this.$postItem({
        endpoint: '/auth/login',
        params: {
          user: params
        }
      })
        .then(({ auth_token }) => {
          const { trackUserLoggedIn } = useTrackingSession()

          this.setAuthToken(auth_token)

          trackUserLoggedIn({ method: 'email' })

          return null
        })
    },
    logout () {
      const { $resetStore: resetUserStore } = useUserStore()
      const { trackUserLoggedOut } = useTrackingSession()

      $cookies.delete({ name: 'authToken' })
      this.$resetStore('auth_user')
      resetUserStore()
      store.commit('auth/setUser', null)

      trackUserLoggedOut()
    }
  }
})